"use client"

/**
 * InstantMessageAd — a display ad styled to mimic an incoming chat/IM
 * message, sitewide (mounted once in app/layout.tsx). Same self-positioning
 * rendering approach as VideoSliderAd/InPagePushAd.
 *
 * Deliberately disabled by default (see instant_message_ads in
 * lib/ad-frequency-store.ts) — this format simulates a real incoming
 * message from a person, a manipulative pattern by design, not just an
 * intrusive one. Wired up and ready, but requires an explicit admin
 * decision to actually turn on, not just building it.
 *
 * AUTO_DISMISS_MS: this and its three siblings (StickyBannerAd,
 * VideoSliderAd, InPagePushAd) previously had NO expiry at all — once
 * `adData` was set, the wrapper (border, badge, our own dismiss button)
 * stayed mounted indefinitely. ExoClick's own "instant message" creative
 * is explicitly a transient-notification format (its delivered CSS uses a
 * `height` transition to animate itself in, per the actual markup pulled
 * live from this zone) — if it animates itself back out or otherwise goes
 * quiet on its own, our side had no way to know and no bound on how long
 * the now-empty card kept sitting there. Reported live as "even when it
 * goes away, they just sit there." This bounds the maximum lifetime
 * regardless of what the creative does internally.
 */
const AUTO_DISMISS_MS = 30_000
const NATIVE_W = 320
const NATIVE_H = 400

import { useEffect, useRef, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useAdTier } from "@/lib/ad-tier"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { isSkipAdsModalActive } from "@/lib/skip-ads"
import { useCornerAdPlacement } from "@/lib/ad-placement"
import { useFixedCornerAdScale } from "@/lib/hooks/use-fixed-corner-ad-scale"
import { useTranslations } from "next-intl"

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
}

export function InstantMessageAd() {
  const t = useTranslations("instantMessageAd")
  const hasConsent = useHasConsent()
  const { isPremiumUser } = useAdStore()
  const { scale, scaledWidth, scaledHeight } = useFixedCornerAdScale(NATIVE_W, NATIVE_H)
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this widget to show for its only realistic tester.
  // See the matching comment on StickyBannerAd.tsx — SkipAdsModal's forced
  // trigger previously only bypassed the "enabled" check, not this premium
  // gate, so it silently rendered nothing for a premium/admin tester
  // outside Force Ads mode despite the modal genuinely triggering it.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn() && !isSkipAdsModalActive()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const triggerCount = useAdTriggerStore((s) => s.instantMessage)
  const lastTriggerRef = useRef(0)
  // Only allowed on the pages lib/ad-placement.ts lists (the VR Browse tab).
  // A manual harness trigger or the Skip Ads modal shows it anywhere.
  const placementOk = useCornerAdPlacement("instant_message")
  const forcedRef = useRef(false)

  useEffect(() => {
    const manuallyTriggered = triggerCount > lastTriggerRef.current
    lastTriggerRef.current = triggerCount
    if (manuallyTriggered) forcedRef.current = true
    if (!hasConsent || effectivePremiumUser) return
    if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return
    // This used to deliberately NOT let a manual trigger bypass the enabled
    // flag here (unlike its 3 sibling corner widgets) — but that made it
    // impossible to preview this placement from the admin harness while
    // it's off, which is exactly the case for all 4 of these right now and
    // exactly what the harness exists to do. Brought in line with the other
    // 3 — see the matching comment on StickyBannerAd.tsx / lib/skip-ads.ts.
    // Wait for the real config before reading the on/off switch and cooldown
    // -- see the matching comment on StickyBannerAd.tsx.
    let ignore = false
    useAdFrequencyStore.getState().fetchConfig().then(() => {
      if (ignore) return
      if (!manuallyTriggered && !useAdFrequencyStore.getState().isEnabled("instant_message_ads") && !isSkipAdsModalActive()) return
      if (!manuallyTriggered) {
        const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("instant_message_ads")
        if (!isAdCooldownExpired("instant_message", cooldown)) return
      }
      if (manuallyTriggered) setDismissed(false)

      fetch(`/api/ads/network-grid-ad?type=instant_message&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ignore) return
          if (d?.zone_id) {
            setAdData(d)
            markAdShown("instant_message")
          }
        })
        .catch(() => {})
    })
    return () => {
      ignore = true
    }
  }, [hasConsent, effectivePremiumUser, adTier, triggerCount, placementOk])

  useEffect(() => {
    if (!adData) return
    const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [adData])

  if (!hasConsent || effectivePremiumUser || !adData || dismissed) return null
  if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return null

  const zoneId = adData.zone_type === "js_embed" ? extractExoclickZoneId(adData.embed_code) : null
  if (!zoneId) return null

  // Bottom-LEFT. Swapped with VideoSliderAd (was here, now bottom-right) —
  // that one's ExoClick zone is configured to slide out from the right, so
  // it needed this corner; this card has no directional animation of its own
  // and reads correctly in either bottom corner. One widget per corner is
  // preserved: StickyBannerAd top-left, InPagePushAd top-right, this
  // bottom-left, VideoSliderAd bottom-right.
  return (
    <div
      className="fixed bottom-4 left-4 z-40 pointer-events-auto"
      style={{ width: scaledWidth, height: scaledHeight }}
    >
      {/* No close button of our own here on purpose — see the git history
          on this line for the two prior attempts (relocate the corner, then
          restyle it) that both turned out wrong. Live screenshots across
          multiple zones confirmed the ExoClick creative reliably paints its
          own real, working close button inside the iframe already; ours was
          never fixing a missing control, it was always just a second one
          stacked next to a real one. Removed rather than relocated again. */}
      <iframe
        src={exoclickIframeSrc(zoneId)}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        title={t("advertisement")}
        scrolling="no"
        // scale === 1 (desktop) renders exactly as before this mobile fix —
        // no transform at all. See the matching comment in VideoSliderAd.tsx.
        style={
          scale >= 1
            ? { width: "100%", height: "100%", border: 0, display: "block", background: "transparent" }
            : {
                width: NATIVE_W,
                height: NATIVE_H,
                border: 0,
                display: "block",
                background: "transparent",
                transform: `scale(${scale})`,
                transformOrigin: "bottom right",
              }
        }
      />
    </div>
  )
}
